/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext } from 'react';
import { css, StyleSheet } from 'aphrodite';
import {
  BrandColors,
  Text,
  LoneIconButton,
  Upload,
  isInProgress as ECisInProgress,
  isUploaded as ECisUploaded,
  ProgressBar,
} from '@blend/enterprise-components';

import { DisablednessContext } from 'blb-contexts/Disabledness';

const styles = StyleSheet.create({
  fileSizeText: {
    marginRight: 8,
  },
  progressBarContainer: {
    height: 10,
    width: 80,
  },
  removeContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  retryContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  retryText: {
    marginRight: 8,
  },
  uploadItemContainer: {
    alignItems: 'center',
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 10,
    padding: 20,
  },
});

export interface UploadItemProps {
  fileSize?: number;
  id: string;
  onRemove(): void;
  onRetry(): void;
  upload: Upload;
}

export const UploadItem: React.FC<UploadItemProps> = ({ upload, onRemove, onRetry, id, fileSize }) => {
  const isInProgress = ECisInProgress(upload);
  const isUploaded = ECisUploaded(upload);
  const isError = !!upload.errorMessage;
  const { disabled } = useContext(DisablednessContext);
  return (
    <div className={css(styles.uploadItemContainer)} id={`it-UploadItem-${id}`}>
      <Text weight="semibold">{upload.fileName!}</Text>
      <div>
        {isInProgress && (
          <div className={css(styles.progressBarContainer)}>
            <ProgressBar
              actionColor={BrandColors.SKY}
              backgroundType="dark"
              borderRadius={5}
              description="UploadItemProgressBar"
              value={upload.progress}
            />
          </div>
        )}
        {isUploaded && (
          <div className={css(styles.removeContainer)}>
            {fileSize && (
              <div className={css(styles.fileSizeText)}>
                <Text>{fileSize}MB</Text>
              </div>
            )}
            <LoneIconButton
              btnSize="sm"
              disabled={disabled}
              icon="delete"
              isCircle
              onClick={onRemove}
              theme="tertiary"
            />
          </div>
        )}
        {isError && (
          <div className={css(styles.retryContainer)}>
            <div className={css(styles.retryText)}>
              <Text colorName="ACCENT_RED">Failed to upload</Text>
            </div>
            <LoneIconButton btnSize="sm" icon="replay" isCircle onClick={onRetry} theme="tertiary" />
          </div>
        )}
      </div>
    </div>
  );
};
